import { isHourAlignedTimeOfDay } from './monitoringScheduleDateUtils';

import type IScheduleConfigDraft from '../interfaces/IScheduleConfigDraft';

export type TMonitoringScheduleValidationField =
  | 'timezone'
  | 'frequency'
  | 'interval'
  | 'daysOfWeek'
  | 'timeOfDay'
  | 'startDate'
  | 'endDate';

export type TMonitoringScheduleValidationErrors = Partial<
Record<TMonitoringScheduleValidationField, string>
>;

export interface IMonitoringScheduleValidationMessages {
  required: string,
  daysOfWeek: string,
  startDatePast: string,
  endDateBeforeStart: string,
  intervalMin: string,
  hourOnly?: string,
}

export const validateMonitoringSchedule = (
  draft: IScheduleConfigDraft,
  messages: IMonitoringScheduleValidationMessages,
  todayLocal = new Date().toISOString().slice(0, 10),
  options: { allowPastStartDate?: boolean } = {},
): TMonitoringScheduleValidationErrors => {
  const errors: TMonitoringScheduleValidationErrors = {};
  const { recurrence } = draft;

  if (!draft.timezone.trim()) {
    errors.timezone = messages.required;
  }

  if (!recurrence.frequency) {
    errors.frequency = messages.required;
  }

  if (!Number.isFinite(recurrence.interval) || recurrence.interval < 1) {
    errors.interval = messages.intervalMin;
  }

  if (recurrence.frequency === 'weekly' && !recurrence.daysOfWeek?.length) {
    errors.daysOfWeek = messages.daysOfWeek;
  }

  if (!recurrence.timeOfDay?.trim()) {
    errors.timeOfDay = messages.required;
  } else if (!isHourAlignedTimeOfDay(recurrence.timeOfDay)) {
    errors.timeOfDay = messages.hourOnly || messages.required;
  }

  if (!recurrence.startDate) {
    errors.startDate = messages.required;
  } else if (!options.allowPastStartDate && recurrence.startDate < todayLocal) {
    errors.startDate = messages.startDatePast;
  }

  if (!draft.noEndDate) {
    if (!recurrence.endDate) {
      errors.endDate = messages.required;
    } else if (recurrence.startDate && recurrence.endDate < recurrence.startDate) {
      errors.endDate = messages.endDateBeforeStart;
    }
  }

  return errors;
};

export const hasMonitoringScheduleValidationErrors = (
  errors: TMonitoringScheduleValidationErrors,
): boolean => Object.keys(errors).length > 0;
